import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';
import Separator from '../../../../../components/Common/UI/Separator';
import { formatMoney } from '../../../../../utils';

interface SummaryCard extends React.PropsWithChildren {
  title: string;
  icon: React.ReactNode;
  extra?: React.ReactNode;
}

function SummaryCard({ title, icon, extra, children }: SummaryCard) {
  return (
    <div className="bg-white p-4 sm:p-7 rounded-[10px] border">
      <div className="card-header mb-5 flex gap-4 justify-between child:flex">
        <div className="gap-2 items-center">
          {icon}
          <span className="text-lg font-bold">{title}</span>
        </div>

        {extra ? <div className="gap-4 items-center flex-wrap">{extra}</div> : null}
      </div>
      {children}
    </div>
  );
}

SummaryCard.defaultProps = {
  extra: undefined,
};

interface AccountItem {
  key: string;
  title: string;
  onlyBalance?: boolean;
  availableMoneyPrefix?: string;
  availableBalance: number | null;
  currentBalance: number | null;
  payAgainBtn?: boolean;
}

const allAccountsData: AccountItem[] = [
  {
    key: '1',
    title: 'ABS Account *026',
    availableMoneyPrefix: '**',
    availableBalance: 13771995.08,
    currentBalance: 13771995.08,
  },
  {
    key: '2',
    title: 'Business  OFX Checking *0101',
    availableMoneyPrefix: '**',
    availableBalance: 1421773,
    currentBalance: 1421773,
  },
  {
    key: '3',
    title: 'OFX Business Checking *0111',
    availableMoneyPrefix: '**',
    availableBalance: 2000,
    currentBalance: 2000,
  },
  {
    key: '4',
    title: 'Simulator Checking *0001',
    availableMoneyPrefix: '**',
    availableBalance: 256333.97,
    currentBalance: 256333.97,
  },
  {
    key: '5',
    title: 'Simulator Loan *0005',
    availableBalance: null,
    currentBalance: 256333.97,
    payAgainBtn: true,
  },
  {
    key: '6',
    title: 'Certificate of Deposit Account *0007',
    availableBalance: null,
    currentBalance: 21343355.65,
    payAgainBtn: false,
  },
];

const limitedAccountsData: AccountItem[] = [
  {
    key: '1',
    title: 'ABS Account *026',
    availableMoneyPrefix: '**',
    availableBalance: 13771995.08,
    currentBalance: 13771995.08,
  },
  {
    key: '2',
    title: 'Business  OFX Checking *0101',
    availableMoneyPrefix: '**',
    availableBalance: 1421773,
    currentBalance: 1421773,
  },
];

const authorizedUserTotals = [
  { label: 'Total Deposit Accounts', value: 15193768.08 },
  { label: 'Total Loans', value: 0 },
];

const regularUserTotals = [
  { label: 'Total Deposit Accounts', value: 35648791 },
  { label: 'Total Loans', value: 1723378.68 },
  { label: 'Total Investment Accounts', value: 21343355.65 },
];

export default function AccountsSummaryContent() {
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

  useEffect(() => {
    const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');

    setIsAuthorizedUser(tempViewers.length > 0);
  }, []);

  const accountsData = isAuthorizedUser ? limitedAccountsData : allAccountsData;

  const totalsData = isAuthorizedUser ? authorizedUserTotals : regularUserTotals;

  return (
    <div>
      <h1 className="text-xl font-bold my-3">Accounts Summary</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <SummaryCard title="Accounts" icon={<Icons.AccountsFilled />}>
            <div className="flex flex-col gap-4">
              {accountsData.map((item, index, arr) => (
                <div key={item.key}>
                  <div className="grid grid-cols-[3fr,2fr]">
                    <div id="col-1" className="text-left">
                      <h3 className="font-bold">{item.title}</h3>
                      {item.availableBalance ? (
                        <>
                          <p className="text-sm">Available</p>
                          <p className="text-muted-foreground">
                            {item.availableMoneyPrefix}
                            {formatMoney(item.availableBalance)}
                          </p>
                        </>
                      ) : null}
                      {!item.availableBalance && item.currentBalance ? (
                        <>
                          <p className="text-sm">Balance</p>
                          <p className="text-muted-foreground">{formatMoney(item.currentBalance)}</p>
                        </>
                      ) : null}
                    </div>

                    <div id="col-2" className="text-right">
                      <p className="text-[#8D59FF] font-bold">Quick peek</p>
                      {/** If both balances exist: */}
                      {item.currentBalance && item.availableBalance ? (
                        <>
                          <p className="text-sm">Current</p>
                          <p className="text-muted-foreground">{formatMoney(item.currentBalance)}</p>
                        </>
                      ) : null}
                      {/** If only current balance with payAgainButton: */}
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {item.currentBalance && !item.availableBalance ? (
                        item.payAgainBtn ? (
                          <Button className="mt-2 text-[#8D59FF] bg-[#8D59FF1A] hover:bg-[#8D59FF2A]" size="sm">
                            <Icons.SolarHistoryBold />
                            Pay
                          </Button>
                        ) : null
                      ) : null}
                    </div>
                  </div>
                  {index !== arr.length - 1 ? <Separator className="mt-4" /> : null}
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="py-1 px-2 rounded-t-lg bg-[#8D59FF] text-white uppercase font-bold">Balance Totals</div>
              <div
                className="py-3 px-2 grid grid-cols-2 gap-3 font-bold text-sm bg-[#8D59FF08]
              border-b border-[#8D59FF33]"
              >
                {totalsData.map(({ label, value }) => (
                  <React.Fragment key={label}>
                    <p className="">{label}</p>
                    <p className="text-right">{formatMoney(value)}</p>
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-[#202020]">
                **This balance may include overdraft or line of credit funds
              </p>
            </div>
          </SummaryCard>
        </div>

        <div className="flex flex-col gap-5">
          <SummaryCard
            title="Money Management"
            icon={<Icons.MoneyManagementFilled />}
            extra={
              <>
                <Button size="sm" variant="default" className="text-[#51CF7C] bg-[#51CF7C1A] hover:bg-[#51cf7c2e]">
                  Spending
                </Button>
                <Button size="sm" variant="default" className="bg-[#51CF7C] hover:bg-[#51cf7cd1]">
                  Budgets
                </Button>
              </>
            }
          >
            <div className="h-28 bg-emerald-50 rounded-lg flex items-center gap-4 p-3">
              <div className="bg-emerald-100 rounded-full h-[70px] w-[70px] flex items-center justify-center">
                <Icons.Safety />
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-medium">Take Control of your money</p>
                <p className="text-xs">Automatically create budgets to help manage your spending</p>
                <Link to="#" className="text-[#51CF7C] underline text-xs">
                  auto-generate Budgets
                </Link>
              </div>
            </div>
          </SummaryCard>

          <SummaryCard title="Outside Accountss" icon={<Icons.OutsideAccountsFilled />}>
            <div className="h-28 bg-orange-50 rounded-lg flex items-center gap-4 p-3">
              <div className="bg-orange-100 rounded-full h-[70px] w-[70px] flex items-center justify-center">
                <Icons.SolarHistoryBold className="text-[#EF9F00CC]" width={28} height={28} />
              </div>
              <div className="flex flex-grow justify-between items-center gap-2">
                <p className="text-xs">No outside accounts added</p>
                <Button size="sm" variant="default" className="bg-[#EF9F00] hover:bg-[#ef9f00e6]">
                  Add Account
                </Button>
              </div>
            </div>
          </SummaryCard>
        </div>
      </div>
    </div>
  );
}
